'use client'

import { ReactNode, createContext, useContext } from 'react'
import {
  Tabs as RadixTabs,
  TabsList as RadixTabsList,
  TabsTrigger as RadixTabsTrigger,
  TabsContent as RadixTabsContent
} from '@teqplay/ui'
import clsx from 'clsx'

import SearchField from '../SearchField/SearchField'

import './VesselListCardLayout.scss'

// Context for passing layout configuration
type LayoutContextType = {
  variant?: 'default' | 'lineup'
}

const LayoutContext = createContext<LayoutContextType>({})

// Main container component
type VesselListCardLayoutProps = {
  children: ReactNode
  className?: string
  variant?: 'default' | 'lineup'
}

function VesselListCardLayoutRoot({
  children,
  className,
  variant = 'default'
}: VesselListCardLayoutProps) {
  return (
    <LayoutContext.Provider value={{ variant }}>
      <section className={clsx('vessel-list-card-layout', className, `variant-${variant}`)}>
        {children}
      </section>
    </LayoutContext.Provider>
  )
}

// Search field component
type SearchFieldProps = {
  className?: string
}

function LayoutSearchField({ className }: SearchFieldProps) {
  return (
    <div className={clsx('layout-header-container', className)}>
      <div className="layout-header">
        <div className="search-container">
          <SearchField />
        </div>
      </div>
    </div>
  )
}

// Tabs wrapper component
type TabsProps = {
  children: ReactNode
  value: string
  onValueChange: (value: string) => void
  className?: string
}

function LayoutTabs({ children, value, onValueChange, className }: TabsProps) {
  return (
    <RadixTabs
      value={value}
      onValueChange={onValueChange}
      className={clsx('layout-tabs', className)}
    >
      {children}
    </RadixTabs>
  )
}

// TabsList component
type TabsListProps = {
  children: ReactNode
  className?: string
  variant?: 'top-level' | 'sub'
}

function LayoutTabsList({ children, className, variant = 'top-level' }: TabsListProps) {
  const variantClass = variant === 'top-level' ? 'top-level-tabs' : 'sub-tabs'
  return <RadixTabsList className={clsx(variantClass, className)}>{children}</RadixTabsList>
}

// TabsTrigger component
type TabsTriggerProps = {
  children: ReactNode
  value: string
  className?: string
}

function LayoutTabsTrigger({ children, value, className }: TabsTriggerProps) {
  return (
    <RadixTabsTrigger value={value} className={className}>
      {children}
    </RadixTabsTrigger>
  )
}

// TabsContent component
type TabsContentProps = {
  children: ReactNode
  value: string
  className?: string
}

function LayoutTabsContent({ children, value, className }: TabsContentProps) {
  return (
    <RadixTabsContent value={value} className={className}>
      {children}
    </RadixTabsContent>
  )
}

// TabTitle component for consistent tab title styling
type TabTitleProps = {
  children: ReactNode
  className?: string
}

function TabTitle({ children, className }: TabTitleProps) {
  return <div className={clsx('tabs-title', className)}>{children}</div>
}

// Loading state component
type LoadingStateProps = {
  className?: string
}

function LoadingState({ className }: LoadingStateProps) {
  const { variant } = useContext(LayoutContext)
  return (
    <div className={clsx('loading-state', className, variant === 'lineup' && 'loading-lineup')}>
      {/* Spinner will be passed as children or handled by parent */}
    </div>
  )
}

// Empty state component
type EmptyStateProps = {
  children: ReactNode
  className?: string
}

function EmptyState({ children, className }: EmptyStateProps) {
  const { variant } = useContext(LayoutContext)
  return (
    <div className={clsx('empty-state', className, variant === 'lineup' && 'empty-lineup')}>
      {children}
    </div>
  )
}

// Assemble the compound component
const VesselListCardLayout = Object.assign(VesselListCardLayoutRoot, {
  SearchField: LayoutSearchField,
  Tabs: LayoutTabs,
  TabsList: LayoutTabsList,
  TabsTrigger: LayoutTabsTrigger,
  TabsContent: LayoutTabsContent,
  TabTitle,
  LoadingState,
  EmptyState
})

export default VesselListCardLayout
